import { ChangesDetection } from '../../../../change-detection';
import { I_ObservationColorRuleResponse } from '../api/observation-color-rule.response';
import { C_ObservationColorRule } from './observation-color-rule.model';
import { I_ConfigObservationResponse } from '../api/config-observation.response';
import { I_UIConfigurationResponse } from '../api/observation.response';
import { Observable, startWith, map, switchMap, shareReplay, of, filter } from 'rxjs';
import { E_ChangeAction } from '../../../../../enums/change-action.enum';
import { E_ArrowType } from '../../../../../enums/observation/observation-arrow-type.enum';
import { E_InsertMode } from '../../../../../enums/observation/observation-insert-mode.enum';

export class C_ConfigObservationList extends ChangesDetection<T_ObsDefChange> {

  private list: Map<string, C_ConfigObservation> = new Map<string, C_ConfigObservation>();
  private cachedSubs = new Map<string, Observable<C_ConfigObservation | undefined>>();

  constructor() {
    super();
    this.forceUpdateDetection();
  }

  getValues(): C_ConfigObservation[] {
    return Array.from(this.list.values());
  }
  has(obsName: string): boolean {
    return this.list.has(obsName);
  }

  get(obsName: string): C_ConfigObservation | undefined {
    return this.list.get(obsName);
  }

  setBulkConfigObs(observations: I_ConfigObservationResponse[]) {
    if (!observations || observations.length === 0) return;

    observations.forEach(obs => {
      const obsKey = obs.name;
      if (!obsKey) return;

      if (!this.list.has(obsKey)) {
        const newObs = new C_ConfigObservation(obs);
        this.list.set(obsKey, newObs);
      } else {
        const currentObs: C_ConfigObservation = this.list.get(obsKey)!;
        currentObs.initializeFromJson(obs);
      }
    });

    // Notificar al final del todo
    this.forceUpdateDetection({
      id: 'BULK',
      action: E_ChangeAction.Setup,
      data: observations
    });
  }

  setConfigObs(obs: I_ConfigObservationResponse) {
    const obsKey = obs.name;
    if (!obsKey) return;
    if (!this.list.has(obsKey)) {
      const newObs = new C_ConfigObservation(obs);
      this.list.set(obsKey, newObs);
      this.forceUpdateDetection({
        id: obsKey,
        action: E_ChangeAction.Add,
        data: newObs
      });
    } else {
      const currentObs: C_ConfigObservation = this.list.get(obsKey)!;
      currentObs.initializeFromJson(obs);
      this.forceUpdateDetection({
        id: obsKey,
        action: E_ChangeAction.Update,
        data: obs
      });
    }
  }

  getLiveConfigObs(name: string): Observable<C_ConfigObservation | undefined> {
    if (this.cachedSubs.has(name)) return this.cachedSubs.get(name)!;

    const stream$ = this.getForcedChanges().pipe(
      // startWith(null) asegura la evaluación inmediata en la primera suscripción
      startWith(null),

      // CRITICAL FILTRATION: Discernir el origen del trigger
      filter(change =>
        change === null ||                     // Permitir el paso del arranque inicial
        change.id === name ||                  // Reevaluar si mutó ESPECÍFICAMENTE esta observación
        change.action === E_ChangeAction.Setup // Reevaluar si hubo una carga masiva completa
      ),

      map(() => this.list.get(name)),
      switchMap(obs => {
        if (!obs) return of(undefined);

        // Escuchamos cambios internos del objeto exclusivamente cuando su propia instancia muta
        return obs.getForcedChanges().pipe(
          startWith(obs),
          map(() => obs)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(name, stream$);
    return stream$;
  }
  clearSubs() {
    this.cachedSubs.clear();
  }

}

export type T_ObsDefChange =
  | { id: string; action: E_ChangeAction.Add; data: C_ConfigObservation }
  | { id: string; action: E_ChangeAction.Update; data: I_ConfigObservationResponse; }
  | { id: string; action: E_ChangeAction.Delete; }
  | { id: string; action: E_ChangeAction.Setup; data: I_ConfigObservationResponse[] };


export class C_ConfigObservation extends ChangesDetection {
  alarm?: any;
  alertColor?: string;
  code?: string;
  name?: string;
  codeSystem?: string;
  codingSystem?: string;
  colorOnExpired?: string;
  expired?: boolean;
  expires?: number;
  level?: number;
  max?: number;
  maxAlert?: number;
  maxWarn?: number;
  min?: number;
  minAlert?: number;
  minWarn?: number;
  warnColor?: string;
  showOnExpired?: boolean;
  persist?: boolean;
  units?: string;
  parentName?: string;
  parentCodingSystem?: string;
  parentCode?: string;
  originalName?: string;
  uiConfiguration?: I_UIConfigurationResponse;
  value?: any;
  showArrow?: boolean;
  arrowType?: E_ArrowType;
  showValue?: boolean;
  colorRules: C_ObservationColorRule[] = [];
  insertMode?: E_InsertMode;
  constructor(json?: I_ConfigObservationResponse) {
    super();
    if (!json) return;
    this.initializeFromJson(json);
  }

  initializeFromJson(json: I_ConfigObservationResponse) {
    if (json.alarm) this.alarm = json.alarm;
    if (json.alertColor) this.alertColor = json.alertColor;
    if (json.code) this.code = json.code;
    if (json.codeSystem) this.codeSystem = json.codeSystem;
    if (json.codingSystem) this.codingSystem = json.codingSystem;
    if (json.colorOnExpired) this.colorOnExpired = json.colorOnExpired;
    if (json.expired) this.expired = json.expired;
    if (json.expires) this.expires = json.expires;
    if (json.level) this.level = json.level;
    if (json.max) this.max = json.max;
    if (json.maxAlert) this.maxAlert = json.maxAlert;
    if (json.maxWarn) this.maxWarn = json.maxWarn;
    if (json.min) this.min = json.min;
    if (json.minAlert) this.minAlert = json.minAlert;
    if (json.minWarn) this.minWarn = json.minWarn;
    if (json.warnColor) this.warnColor = json.warnColor;
    if (json.showOnExpired) this.showOnExpired = json.showOnExpired;
    if (json.persist) this.persist = json.persist;
    if (json.units) this.units = json.units;
    if (json.parentName) this.parentName = json.parentName;
    if (json.parentCodingSystem) this.parentCodingSystem = json.parentCodingSystem;
    if (json.parentCode) this.parentCode = json.parentCode;
    if (json.originalName) this.originalName = json.originalName;
    if (json.name) this.name = json.name;
    if (json.uiConfiguration) this.uiConfiguration = json.uiConfiguration;
    if (json.showArrow != null) this.showArrow = json.showArrow;
    if (json.arrowType != null) this.arrowType = json.arrowType;
    else this.arrowType = E_ArrowType.Default;
    if (json.showValue != null) this.showValue = json.showValue;
    if (json.insertMode != null) this.insertMode = json.insertMode;
    if (json.colorRanges) {
      this.colorRules = [];
      json.colorRanges.forEach((cr: I_ObservationColorRuleResponse) => {
        this.colorRules.push(new C_ObservationColorRule(cr));
      });
    }
    this.forceUpdateDetection();
  }
}
